import { SocketState } from "@components/Context/SocketContext";
import Header from "@components/Layout/Header";
import http from "@services/http";
import { RootDispatch } from "@store";
import { logout } from "@store/auth/actions";
import { isTokenAlive } from "@store/auth/cookieStorage";
import { ROUTES_E } from "@utils/RoutsName";
import { SuspendComponent } from "@utils/SuspenseComponents";
import { saveLangToLocal } from "@utils/lang";
import { ConfigProvider } from "antd";
import enUS from "antd/lib/locale/en_US";
import { LazyMotion, domAnimation } from "framer-motion";
import moment from "moment";
import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { Route, BrowserRouter, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { IS_DEBUG } from "../../config";
import { LocaleContext, LocaleLangE } from "../../i18n/LocaleContext";
import ProviderIntl, { GlobalIntl } from "../../i18n/ProviderIntl";
import useCurrentUser, {
  useAllContractsUpdater,
} from "../../utils/hooks/useCurrentUser";
import AUTHSTATES from "@store/auth/AuthStates";
import { useAuth } from "@utils/hooks/useAuth";
import "dayjs/locale/en";
import "dayjs/locale/ru";
import { Menu } from "@components/Layout/Menu";
import appStyles from "../../styles/App.module.scss";
import badge from "../../styles/badge.module.scss";
import button from "./styles/button.module.scss";
import checkbox from "./styles/checkbox.module.scss";
import radio from "../../styles/radio.module.scss";
import timePicker from "../../styles/timePicker.module.scss";
import select from "../../styles/select.module.scss";
import input from "../../styles/input.module.scss";
import switchStyle from "../../styles/switch.module.scss";
import tabs from "./styles/tabs.module.scss";
import transfer from "../../styles/transfer.module.scss";
import "./styles/recaptcha.scss";
import classNames from "classnames";
import { ScrollableArea } from "@components/global/new/ScrollableArea/ScrollableArea";
import { ComponentsE } from "../../App";
import LandingPage from "./pages/LandingPage/LandingPage";
import { WlpThemeShell } from "./theme";

const menu = [
  {
    location: ROUTES_E.MACHINES,
    id: "menu.machines",
    onlyAdmin: false,
  },
  {
    location: ROUTES_E.CLIENTS,
    id: "menu.clients",
    onlyAdmin: true,
  },
  {
    location: ROUTES_E.LOCATIONS,
    id: "menu.locations_login",
    onlyAdmin: false,
  },
  {
    location: ROUTES_E.SETTINGS,
    id: "menu.settings",
    onlyAdmin: false,
  },
  {
    location: ROUTES_E.MANAGMENT,
    id: "menu.managment",
    onlyAdmin: false,
  },
  {
    location: ROUTES_E.MESSENGER,
    id: "menu.messenger",
    onlyAdmin: false,
  },
  {
    location: ROUTES_E.NOTIFICATIONS,
    id: "menu.notifications",
    onlyAdmin: false,
  },
  {},
];

function WlpApp() {
  const locale = LocaleLangE.EN;
  const auth = useAuth();
  const dispatch = useDispatch<RootDispatch>();
  const interval = useRef<ReturnType<typeof setInterval>>();
  const contractsUpdater = useAllContractsUpdater();

  useEffect(() => {
    http.utils.getTime().catch(() => undefined);
  }, []);

  useEffect(() => {
    if (auth.state === AUTHSTATES.AUTHORIZED) {
      if (!isTokenAlive()) dispatch(logout("WlpApp"));
      if (interval.current) {
        clearInterval(interval.current);
        interval.current = undefined;
      }
      interval.current = setInterval(() => {
        if (!isTokenAlive()) dispatch(logout("WlpApp"));
      }, 10 * 1000);
      contractsUpdater();
    } else if (interval.current) {
      clearInterval(interval.current);
      interval.current = undefined;
    }
  }, [auth.state]);

  useCurrentUser();

  useEffect(() => {
    saveLangToLocal(locale);
    moment.locale(locale.toLocaleLowerCase());
  }, [locale]);

  return (
    <div className={appStyles.app}>
      <LazyMotion features={domAnimation}>
        <LocaleContext.Provider value={{ locale, setLocale: () => undefined }}>
          <ConfigProvider
            locale={enUS}
            button={{ className: button.button }}
            checkbox={{ className: checkbox.checkbox }}
            input={{ className: input.input }}
            select={{ className: select.select }}
            switch={{ className: switchStyle.switch }}
            tabs={{ className: tabs.tabs }}
            transfer={{ className: transfer.transfer }}
            badge={{ className: badge.badge }}
            radio={{ className: radio.radio }}
            timePicker={{ className: timePicker.timePicker }}
          >
            <SocketState>
              <BrowserRouter>
                <ProviderIntl locale={locale}>
                  <GlobalIntl />
                  <ToastContainer toastClassName={"toasts__global__class"} />
                  {auth.state === AUTHSTATES.AUTHORIZED && (
                    <Menu isAdmin={auth.isAdmin} items={menu} />
                  )}
                  <div
                    className={classNames(appStyles.main, {
                      [appStyles.auth]: auth.state === AUTHSTATES.AUTHORIZED,
                      [appStyles.preview]: auth.state !== AUTHSTATES.AUTHORIZED,
                    })}
                  >
                    {auth.state === AUTHSTATES.AUTHORIZED && <Header menu={menu} />}
                    <div
                      className={classNames(appStyles.content, {
                        [appStyles.auth]: auth.state === AUTHSTATES.AUTHORIZED,
                        [appStyles.preview]: auth.state !== AUTHSTATES.AUTHORIZED,
                      })}
                    >
                      {auth.state === AUTHSTATES.AUTHORIZED ? (
                        <WlpThemeShell>
                          <ScrollableArea disableHorizontal>
                            <Routes>
                              <Route path={ROUTES_E.HOME} element={<LandingPage />} />
                              <Route
                                path={ROUTES_E.LOGIN}
                                element={SuspendComponent(ComponentsE.LOGIN)}
                              />
                              <Route
                                path={ROUTES_E.ACCOUNT}
                                element={SuspendComponent(ComponentsE.USER)}
                              />
                              <Route
                                path={ROUTES_E.MACHINES}
                                element={SuspendComponent(ComponentsE.MACHINES)}
                              />
                              {IS_DEBUG && auth.isAdmin && (
                                <Route
                                  path={ROUTES_E.SPAREPARTS}
                                  element={SuspendComponent(ComponentsE.SPAREPARTS)}
                                />
                              )}
                              <Route
                                path={ROUTES_E.CLIENTS}
                                element={SuspendComponent(ComponentsE.CLIENTS)}
                              />
                              <Route
                                path={ROUTES_E.SETTINGS}
                                element={SuspendComponent(ComponentsE.SETTINGS)}
                              />
                              <Route
                                path={ROUTES_E.LOCATIONS}
                                element={SuspendComponent(ComponentsE.LOCATIONS)}
                              />
                              <Route
                                path={ROUTES_E.MANAGMENT}
                                element={SuspendComponent(ComponentsE.CONTRACTS)}
                              />
                              <Route
                                path={ROUTES_E.MESSENGER}
                                element={SuspendComponent(ComponentsE.MESSENGER)}
                              />
                              <Route
                                path={ROUTES_E.NOTIFICATIONS}
                                element={SuspendComponent(ComponentsE.NOTIFICATIONS)}
                              />
                              <Route
                                path={ROUTES_E.PARAMS}
                                element={SuspendComponent(ComponentsE.PARAMS)}
                              />
                              <Route
                                path={ROUTES_E.ERROR}
                                element={SuspendComponent(ComponentsE.ERROR)}
                              />
                              <Route
                                path={ROUTES_E.INFO}
                                element={SuspendComponent(ComponentsE.INFO)}
                              />
                              <Route
                                path={ROUTES_E.SCANREPORT}
                                element={SuspendComponent(ComponentsE.SCANREPORT)}
                              />
                              {IS_DEBUG && (
                                <Route
                                  path={ROUTES_E.TEST_CHART}
                                  element={SuspendComponent(ComponentsE.TEST_CHART)}
                                />
                              )}
                              <Route
                                path={"*"}
                                element={SuspendComponent(ComponentsE.ERROR_404)}
                              />
                            </Routes>
                          </ScrollableArea>
                        </WlpThemeShell>
                      ) : (
                        <div style={{ height: "100%", overflowY: "auto", overflowX: "hidden" }}>
                          <Routes>
                            <Route path={ROUTES_E.HOME} element={<LandingPage />} />
                            <Route path={"*"} element={<LandingPage />} />
                          </Routes>
                        </div>
                      )}
                    </div>
                  </div>
                </ProviderIntl>
              </BrowserRouter>
            </SocketState>
          </ConfigProvider>
        </LocaleContext.Provider>
      </LazyMotion>
    </div>
  );
}

export default WlpApp;
